/**
 * Store Filter Hook
 * Provides reusable store filtering logic with location-based filtering
 * Shows all stores initially, then filters within radius when location is provided
 * Distances are calculated and displayed in miles
 *
 * Performance Optimizations:
 * - Supports cached stores from Redux to avoid redundant Firebase queries
 * - Lazy loading option with autoFetch parameter
 * - Memoized distance calculations
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { firestore } from '../../../../../libs/firebase-service/src';
import { calculateDistance } from '../services/locationService';

export const useStoreFilter = ({
  enableConsoleLogging = true,
  radiusMiles = 7, // Default ~7 miles
  cachedStores = null, // Optional: pre-fetched stores from Redux
  autoFetch = true, // Whether to auto-fetch stores on mount
}) => {
  const [allStores, setAllStores] = useState(cachedStores || []);
  const [filteredStores, setFilteredStores] = useState(cachedStores || []);
  const [selectedStore, setSelectedStore] = useState(null);
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [storeError, setStoreError] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  // The device's GPS location used as the canonical initial distance origin
  const [deviceLocationForDistance, setDeviceLocationForDistance] =
    useState(null);

  const log = useCallback(
    (...args) => {
      if (enableConsoleLogging) {
        console.log('[useStoreFilter]', ...args);
      }
    },
    [enableConsoleLogging]
  );

  // Add distances to stores from user location (without filtering by radius)
  const addDistancesToStores = useCallback(
    (stores, location) => {
      if (!location?.lat || !location?.lng || !stores?.length) {
        return stores;
      }

      // log(`📏 Adding distances to stores from:`, {
      //   lat: location.lat,
      //   lng: location.lng,
      //   displayName: location.displayName || 'User location',
      // });

      return stores.map((store) => {
        const storeLocation = store._geoloc || store.address;
        const distance = calculateDistance(location, storeLocation);
        // convert only if distance is a finite number (0 is valid)
        const distanceInMiles = Number.isFinite(distance)
          ? distance * 0.621371
          : null; // Convert km to miles

        if (!Number.isFinite(distanceInMiles)) {
          // log(
          //   `❌ Store "${
          //     store.businessName || store.name
          //   }" has invalid coordinates`
          // );
          return store; // Return store without distance
        }

        return {
          ...store,
          distance: distanceInMiles,
          storeLocation: {
            lat: storeLocation.lat,
            lng: storeLocation.lng,
          },
        };
      });
    },
    [log]
  );

  // Helper to sort stores by their distance property (nearest first)
  const sortByDistance = useCallback((storesArr) => {
    if (!storesArr || !storesArr.length) return storesArr;
    return [...storesArr].sort((a, b) => {
      const da = a?.distance;
      const db = b?.distance;
      if (da == null && db == null) return 0;
      if (da == null) return 1; // push entries without distance to the end
      if (db == null) return -1;
      return da - db;
    });
  }, []);

  // Get user's current GPS location
  const getCurrentLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        const error = 'Geolocation is not supported by this browser';
        log('❌', error);
        reject(new Error(error));
        return;
      }

      // log("🌍 Requesting user's current GPS location...");

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            displayName: 'Current Location',
            accuracy: position.coords.accuracy,
          };

          // log('✅ GPS location obtained:', location);
          resolve(location);
        },
        (error) => {
          log('❌ Geolocation error:', error.message);
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    });
  }, [log]);

  // Fetch all stores from Firebase
  const fetchAllStores = useCallback(async () => {
    try {
      setIsLoadingStores(true);
      setStoreError(null);
      // log('🔄 Fetching all stores from Firebase...');

      const storesSnapshot = await firestore
        .collection('stores')
        .where('is_deleted', '==', false)
        .get();

      const storesData = storesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // log(`✅ Found ${storesData.length} total stores`);
      setAllStores(storesData);

      // Try to get user location for distance calculation
      try {
        // log('🌍 Attempting to get user location for distance calculation...');
        const currentLocation = await getCurrentLocation();
        // Save device GPS as the canonical source for distances
        setDeviceLocationForDistance(currentLocation);

        // Add distances to all stores using device GPS and sort by distance
        const storesWithDistance = addDistancesToStores(
          storesData,
          currentLocation
        );
        setFilteredStores(sortByDistance(storesWithDistance));
        // log(
        //   '📋 Displaying all stores with distances from user location (sorted)'
        // );
      } catch (locationError) {
        log('⚠️ Could not get user location, showing stores without distances');
        // Still show all stores even if location fails
        setFilteredStores(storesData);
      }

      return storesData;
    } catch (error) {
      log('❌ Error fetching stores:', error);
      setStoreError('Failed to load stores');
      return [];
    } finally {
      setIsLoadingStores(false);
    }
  }, [log, getCurrentLocation, addDistancesToStores]);

  // Filter stores within radius and sort by distance
  const filterStoresByLocation = useCallback(
    (location, stores = allStores) => {
      if (!location?.lat || !location?.lng) {
        log('⚠️ No valid location provided, showing all stores');
        return stores;
      }

      if (!stores?.length) {
        log('⚠️ No stores available for filtering');
        return [];
      }

      // log(`🎯 Filtering stores within ${radiusMiles} miles radius of:`, {
      //   lat: location.lat,
      //   lng: location.lng,
      //   displayName: location.displayName || 'Unknown location',
      // });

      const storesWithDistance = stores
        .map((store) => {
          // Get store coordinates from _geoloc or address
          const storeLocation = store._geoloc || store.address;
          const distance = calculateDistance(location, storeLocation);
          const distanceInMiles = Number.isFinite(distance)
            ? distance * 0.621371
            : null; // Convert km to miles

          if (!Number.isFinite(distanceInMiles)) {
            // log(
            //   `❌ Store "${
            //     store.businessName || store.name
            //   }" has invalid coordinates`
            // );
            return null;
          }

          const withinRadius = distanceInMiles <= radiusMiles;
          // log(
          //   `📍 ${
          //     store.businessName || store.name
          //   }: ${distanceInMiles.toFixed(2)} miles ${
          //     withinRadius ? '✅' : '❌'
          //   }`
          // );

          return {
            ...store,
            distance: distanceInMiles,
            storeLocation: {
              lat: storeLocation.lat,
              lng: storeLocation.lng,
            },
          };
        })
        .filter((store) => store && store.distance <= radiusMiles) // Filter within radius
        .sort((a, b) => a.distance - b.distance); // Sort by distance

      // log(
      //   `🏪 ${storesWithDistance.length} stores found within ${radiusMiles} miles radius`
      // );

      // if (storesWithDistance.length > 0) {
      //   log('📊 Final filtered and sorted stores:');
      //   storesWithDistance.forEach((store, index) => {
      //     log(
      //       `  ${index + 1}. ${
      //         store.businessName || store.name
      //       } (${store.distance.toFixed(2)} miles)`
      //     );
      //   });
      // } else {
      //   log('🔍 No stores found within the specified radius');
      // }

      return storesWithDistance;
    },
    [allStores, radiusMiles, log]
  );

  // Update location and apply filtering
  const updateLocation = useCallback(
    (newLocation) => {
      // log('📍 Updating user location:', newLocation);
      setUserLocation(newLocation);
      if (newLocation) {
        const filtered = filterStoresByLocation(newLocation);
        setFilteredStores(filtered);
      } else {
        // log('📋 No location provided, showing all stores');
        // When clearing location filter, still show distances from user's current location if available
        if (deviceLocationForDistance) {
          const storesWithDistance = addDistancesToStores(
            allStores,
            deviceLocationForDistance
          );
          setFilteredStores(sortByDistance(storesWithDistance));
        } else {
          setFilteredStores(allStores);
        }
      }
    },
    [
      filterStoresByLocation,
      allStores,
      deviceLocationForDistance,
      addDistancesToStores,
      log,
    ]
  );

  // Use current location and filter stores
  const useCurrentLocation = useCallback(async () => {
    try {
      // log('🎯 Getting current location for store filtering...');
      const currentLocation = await getCurrentLocation();
      updateLocation(currentLocation);
      return currentLocation;
    } catch (error) {
      log('❌ Failed to get current location:', error.message);
      throw error;
    }
  }, [getCurrentLocation, updateLocation, log]);

  // Clear location filter and show all stores
  // preserveAddress: when true (default) do not clear the selected address
  // from the hook state; only remove the radius filter and show all stores
  // while preserving distance calculations from the selected address if present.
  // When false, clear the selected address and prefer user's GPS location
  // for distance calculations (if available).
  const clearLocationFilter = useCallback(
    (preserveAddress = true) => {
      // log('🗑️ Clearing location filter, showing all stores', {
      //   preserveAddress,
      // });

      if (!preserveAddress) {
        // Clear the selected address in the hook
        setUserLocation(null);
      }

      // If we have a selected address (and we were asked to preserve it),
      // show all stores but compute distances from that address.
      if (preserveAddress && userLocation) {
        const storesWithDistance = addDistancesToStores(
          allStores,
          userLocation
        );
        setFilteredStores(sortByDistance(storesWithDistance));
        // log(
        //   '📏 Showing all stores with distances from selected address (sorted)'
        // );
        return;
      }

      // Otherwise prefer distances from the device GPS location if available
      if (deviceLocationForDistance) {
        const storesWithDistance = addDistancesToStores(
          allStores,
          deviceLocationForDistance
        );
        setFilteredStores(sortByDistance(storesWithDistance));
        // log('📏 Showing all stores with distances from user location (sorted)');
      } else {
        setFilteredStores(allStores);
        // log('📋 Showing all stores without distances');
      }
    },
    [
      allStores,
      userLocation,
      deviceLocationForDistance,
      addDistancesToStores,
      log,
    ]
  );

  // Manually select a store
  const selectStore = useCallback(
    (store) => {
      setSelectedStore(store);
      // log(`✅ Manually selected store: ${store.businessName || store.name}`);
    },
    [log]
  );

  // Initialize stores on mount - only if autoFetch is true and no cached stores
  useEffect(() => {
    if (autoFetch && (!cachedStores || cachedStores.length === 0)) {
      fetchAllStores();
    } else if (cachedStores && cachedStores.length > 0) {
      // Use cached stores and calculate distances
      log(`✅ Using ${cachedStores.length} cached stores`);
      setAllStores(cachedStores);

      // Try to add distances from user's current location
      getCurrentLocation()
        .then((currentLocation) => {
          setDeviceLocationForDistance(currentLocation);
          const storesWithDistance = addDistancesToStores(
            cachedStores,
            currentLocation
          );
          setFilteredStores(sortByDistance(storesWithDistance));
        })
        .catch(() => {
          // If location fails, just show cached stores without distances
          setFilteredStores(cachedStores);
        });
    }
  }, [
    autoFetch,
    cachedStores,
    fetchAllStores,
    getCurrentLocation,
    addDistancesToStores,
    sortByDistance,
    log,
  ]);

  // Refresh stores
  const refreshStores = useCallback(() => {
    // log('🔄 Refreshing store data...');
    fetchAllStores();
  }, [fetchAllStores, log]);

  return {
    // Store data
    allStores,
    filteredStores,
    selectedStore,
    userLocation,
    deviceLocationForDistance,

    // State
    isLoadingStores,
    storeError,

    // Actions
    selectStore,
    updateLocation,
    useCurrentLocation,
    clearLocationFilter,
    refreshStores,
    fetchStores: fetchAllStores, // Expose fetch function for manual triggering

    // Utils
    filterStoresByLocation,
    getCurrentLocation,
    addDistancesToStores,

    // Config
    radiusMiles,
  };
};

export default useStoreFilter;
